'use client';

import { type ComponentProps } from 'react';
import {
    Root,
    Portal,
    Trigger,
    Content,
    Group,
    Item,
    CheckboxItem,
    ItemIndicator,
    RadioGroup,
    RadioItem,
    Label,
    Separator,
    Sub,
    SubTrigger,
    SubContent,
} from '@radix-ui/react-dropdown-menu';
import { CheckIcon, ChevronRightIcon, CircleIcon, User } from 'lucide-react';

import { cn } from '@/util/utils';

interface DropdownMenuItemProps extends ComponentProps<typeof Item>, DropdownMenuInsetProps {
    variant?: DropdownMenuItemVariant;
}

interface DropdownMenuInsetProps {
    inset?: boolean;
}

enum DropdownMenuItemVariant {
    DEFAULT = 'default',
    DESTRUCTIVE = 'destructive',
}

function DropdownMenu({ dir, open, defaultOpen, modal, ...props }: ComponentProps<typeof Root>) {
    return (
        <Root
            data-slot="dropdown-menu"
            dir={dir}
            open={open}
            defaultOpen={defaultOpen}
            modal={modal}
            {...props}
        />
    );
}

function DropdownMenuPortal({ container, forceMount, ...props }: ComponentProps<typeof Portal>) {
    return (
        <Portal
            data-slot="dropdown-menu-portal"
            container={container}
            forceMount={forceMount}
            {...props}
        />
    );
}

function DropdownMenuTrigger({ ref, asChild, ...props }: ComponentProps<typeof Trigger>) {
    return <Trigger ref={ref} asChild={asChild} data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
    className,
    sideOffset = 4,
    forceMount,
    onCloseAutoFocus,
    loop,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    side,
    align,
    alignOffset,
    arrowPadding,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Content>) {
    return (
        <Portal>
            <Content
                data-slot="dropdown-menu-content"
                sideOffset={sideOffset}
                forceMount={forceMount}
                onCloseAutoFocus={onCloseAutoFocus}
                loop={loop}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={onPointerDownOutside}
                onFocusOutside={onFocusOutside}
                onInteractOutside={onInteractOutside}
                side={side}
                align={align}
                alignOffset={alignOffset}
                arrowPadding={arrowPadding}
                avoidCollisions={avoidCollisions}
                collisionBoundary={collisionBoundary}
                collisionPadding={collisionPadding}
                sticky={sticky}
                hideWhenDetached={hideWhenDetached}
                updatePositionStrategy={updatePositionStrategy}
                asChild={asChild}
                ref={ref}
                className={cn(
                    'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) max-w-56 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md',
                    'bg-card text-foreground border-border flex min-w-[228px] flex-col gap-0.5 rounded-lg border p-1 shadow-lg',
                    className,
                )}
                {...props}
            />
        </Portal>
    );
}

function DropdownMenuGroup({ asChild, ref, ...props }: ComponentProps<typeof Group>) {
    return <Group data-slot="dropdown-menu-group" asChild={asChild} ref={ref} {...props} />;
}

function DropdownMenuItem({
    className,
    inset,
    variant = DropdownMenuItemVariant.DEFAULT,
    onSelect,
    disabled,
    textValue,
    asChild,
    ref,
    ...props
}: DropdownMenuItemProps) {
    return (
        <Item
            data-slot="dropdown-menu-item"
            data-inset={inset}
            data-variant={variant}
            onSelect={onSelect}
            disabled={disabled}
            textValue={textValue}
            asChild={asChild}
            ref={ref}
            className={cn(
                "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-normal tracking-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                'focus:bg-muted focus:text-foreground data-[variant=destructive]:focus:bg-destructive-muted [&_svg]:text-muted-foreground data-[variant=destructive]:[&_svg]:text-destructive items-start gap-2 rounded-md px-2.5 py-2 text-sm [&_svg]:mt-0.5 [&_svg:not([class*=size-])]:size-[15px]',
                className,
            )}
            {...props}
        />
    );
}

function DropdownMenuCheckboxItem({
    className,
    children,
    checked,
    onCheckedChange,
    onSelect,
    disabled,
    textValue,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof CheckboxItem>) {
    return (
        <CheckboxItem
            data-slot="dropdown-menu-checkbox-item"
            className={cn(
                "focus:bg-accent focus:text-accent-foreground hover:bg-accent relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm font-normal tracking-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            checked={checked}
            onCheckedChange={onCheckedChange}
            onSelect={onSelect}
            disabled={disabled}
            textValue={textValue}
            asChild={asChild}
            ref={ref}
            {...props}
        >
            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                <ItemIndicator>
                    <CheckIcon className="size-4" />
                </ItemIndicator>
            </span>
            {children}
        </CheckboxItem>
    );
}

function DropdownMenuRadioGroup({
    value,
    onValueChange,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof RadioGroup>) {
    return (
        <RadioGroup
            data-slot="dropdown-menu-radio-group"
            value={value}
            onValueChange={onValueChange}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function DropdownMenuRadioItem({
    className,
    children,
    value,
    onSelect,
    disabled,
    textValue,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof RadioItem>) {
    return (
        <RadioItem
            data-slot="dropdown-menu-radio-item"
            className={cn(
                "focus:bg-accent focus:text-accent-foreground hover:bg-accent relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm font-normal tracking-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            value={value}
            onSelect={onSelect}
            disabled={disabled}
            textValue={textValue}
            asChild={asChild}
            ref={ref}
            {...props}
        >
            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                <ItemIndicator>
                    <CircleIcon className="size-2 fill-current" />
                </ItemIndicator>
            </span>
            {children}
        </RadioItem>
    );
}

function DropdownMenuLabel({
    className,
    inset,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Label> & DropdownMenuInsetProps) {
    return (
        <Label
            data-slot="dropdown-menu-label"
            data-inset={inset}
            asChild={asChild}
            ref={ref}
            className={cn(
                'text-label-sm text-muted-foreground px-2 pt-2 pb-1 data-[inset]:pl-8',
                className,
            )}
            {...props}
        />
    );
}

function DropdownMenuSeparator({
    className,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Separator>) {
    return (
        <Separator
            data-slot="dropdown-menu-separator"
            asChild={asChild}
            ref={ref}
            className={cn('bg-border h-px', className)}
            {...props}
        />
    );
}

function DropdownMenuShortcut({ className, ref, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="dropdown-menu-shortcut"
            ref={ref}
            className={cn('text-muted-foreground ml-auto text-xs', className)}
            {...props}
        />
    );
}

function DropdownMenuSub({ open, defaultOpen, ...props }: ComponentProps<typeof Sub>) {
    return <Sub data-slot="dropdown-menu-sub" open={open} defaultOpen={defaultOpen} {...props} />;
}

function DropdownMenuSubTrigger({
    className,
    inset,
    children,
    disabled,
    textValue,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof SubTrigger> & DropdownMenuInsetProps) {
    return (
        <SubTrigger
            data-slot="dropdown-menu-sub-trigger"
            data-inset={inset}
            disabled={disabled}
            textValue={textValue}
            asChild={asChild}
            ref={ref}
            className={cn(
                'focus:bg-accent data-[state=open]:bg-accent active:bg-accent flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm font-normal outline-hidden select-none data-[inset]:pl-8',
                'focus:bg-muted data-[state=open]:bg-muted active:bg-muted cursor-pointer gap-2 rounded-md px-2.5 py-2',
                className,
            )}
            {...props}
        >
            {children}
            <ChevronRightIcon className="ml-auto size-4" />
        </SubTrigger>
    );
}

function DropdownMenuSubContent({
    className,
    forceMount,
    loop,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    sideOffset,
    alignOffset,
    arrowPadding,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof SubContent>) {
    return (
        <SubContent
            data-slot="dropdown-menu-sub-content"
            forceMount={forceMount}
            loop={loop}
            onEscapeKeyDown={onEscapeKeyDown}
            onPointerDownOutside={onPointerDownOutside}
            onFocusOutside={onFocusOutside}
            onInteractOutside={onInteractOutside}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            arrowPadding={arrowPadding}
            avoidCollisions={avoidCollisions}
            collisionBoundary={collisionBoundary}
            collisionPadding={collisionPadding}
            sticky={sticky}
            hideWhenDetached={hideWhenDetached}
            updatePositionStrategy={updatePositionStrategy}
            asChild={asChild}
            ref={ref}
            className={cn(
                'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg',
                'bg-card text-foreground border-border flex min-w-[228px] flex-col gap-0.5 rounded-lg border p-1 shadow-lg',
                className,
            )}
            {...props}
        />
    );
}

function DropdownMenuIconItem({
    className,
    children,
    checked,
    onCheckedChange,
    onSelect,
    disabled,
    textValue,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof CheckboxItem>) {
    return (
        <CheckboxItem
            data-slot="dropdown-menu-checkbox-item"
            checked={checked}
            onCheckedChange={onCheckedChange}
            onSelect={onSelect}
            disabled={disabled}
            textValue={textValue}
            asChild={asChild}
            ref={ref}
            className={cn(
                "focus:bg-accent focus:text-accent-foreground [&_svg]:text-muted-foreground hover:bg-accent aria-invalid:text-destructive aria-invalid:[&_svg]:text-destructive relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm font-normal tracking-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        >
            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                <ItemIndicator>
                    <User className="size-4" />
                </ItemIndicator>
            </span>
            {children}
        </CheckboxItem>
    );
}

export {
    DropdownMenu,
    DropdownMenuPortal,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
    DropdownMenuIconItem,
    DropdownMenuItemVariant,
};
